/**
 * Language Toggle Button
 * Dropdown component for language switching across all portals
 * Supports English, Arabic, and Urdu
 */

import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { cn } from '../../../lib/utils'
import { Globe, Check } from '../../../icons'
import type { LanguageToggleProps } from './types'
import { BUTTON_SIZES, ICON_SIZES, LANGUAGE_OPTIONS } from './constants'

// ============================================================================
// Helper Functions
// ============================================================================

function getButtonStyles(isMobile?: boolean): string {
  if (isMobile) {
    return cn(BUTTON_SIZES.MOBILE, 'rounded-xl hover:bg-secondary transition-all')
  }
  return 'h-11 px-4 gap-2 font-medium rounded-xl hover:bg-secondary transition-all'
}

function getCurrentLanguageLabel(currentLang: string): string {
  const lang = LANGUAGE_OPTIONS.find(l => l.code === currentLang)
  return lang?.nativeLabel ?? 'English'
}

// ============================================================================
// Component
// ============================================================================

export function LanguageToggle({
  currentLang,
  onToggle,
  onChange,
  isMobile = false
}: LanguageToggleProps) {
  const handleLanguageChange = (langCode: string) => {
    if (onChange) {
      onChange(langCode)
    } else if (onToggle) {
      // Legacy toggle behavior - just call onToggle
      onToggle()
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={isMobile ? 'icon' : 'sm'}
          className={getButtonStyles(isMobile)}
          aria-label="Switch language"
        >
          <Globe className={cn(ICON_SIZES.SMALL, 'text-muted-foreground')} />
          {!isMobile && (
            <span className="text-sm text-foreground">{getCurrentLanguageLabel(currentLang)}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {LANGUAGE_OPTIONS.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className={cn(
              lang.code === 'ar' || lang.code === 'ur' ? 'font-arabic' : ''
            )}>
              {lang.nativeLabel}
            </span>
            {currentLang === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
